import { router } from "@inertiajs/react";
import { useEffect, useState } from "react";
import Header from "../Component/Header";

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 2,
    }).format(Number(value || 0));

const commissionStatusClass = (status) => {
    switch (Number(status)) {
        case 1:
            return "bg-blue-100 text-blue-700";
        case 2:
            return "bg-green-100 text-green-700";
        case 3:
            return "bg-red-100 text-red-700";
        default:
            return "bg-amber-100 text-amber-700";
    }
};

export default function Index({ commissions, filters = {} }) {
    const [notification, setNotification] = useState(null);
    const [busyKey, setBusyKey] = useState("");
    const [selectedCommissionIds, setSelectedCommissionIds] = useState([]);
    const [search, setSearch] = useState(filters.search ?? "");
    const [week, setWeek] = useState(filters.week ?? "");
    const [perPage, setPerPage] = useState(String(filters.per_page ?? 10));

    const commissionRows = Array.isArray(commissions?.data) ? commissions.data : [];

    useEffect(() => {
        setSearch(filters.search ?? "");
        setWeek(filters.week ?? "");
        setPerPage(String(filters.per_page ?? 10));
    }, [filters.search, filters.week, filters.per_page]);

    const reloadPage = () => window.location.reload();
    const visitWithFilters = (nextFilters = {}) => {
        router.get(
            "/panel/reseller-commissions",
            {
                search,
                week,
                per_page: perPage,
                ...nextFilters,
            },
            {
                preserveState: true,
                replace: true,
            }
        );
    };

    const selectableCommissions = commissionRows.filter(
        (commission) =>
            Number(commission.commission_status) === 1 &&
            !commission.payout_id
    );
    const selectableIds = selectableCommissions.map((commission) => commission.soc_id);
    const allSelectableChecked =
        selectableIds.length > 0 &&
        selectableIds.every((id) => selectedCommissionIds.includes(id));
    const selectedCommissions = commissionRows.filter((commission) =>
        selectedCommissionIds.includes(commission.soc_id)
    );
    const selectedMemberIds = [...new Set(selectedCommissions.map((commission) => commission.mem_id))];
    const selectedResellerNames = [
        ...new Set(
            selectedCommissions.map(
                (commission) => commission.member?.user?.name || "Unknown reseller"
            )
        ),
    ];
    const selectedTotal = selectedCommissions.reduce(
        (total, commission) => total + Number(commission.commission_amount ?? 0),
        0
    );

    const toggleCommission = (commission) => {
        const isSelectable =
            Number(commission.commission_status) === 1 && !commission.payout_id;
        if (!isSelectable) return;

        setSelectedCommissionIds((prev) =>
            prev.includes(commission.soc_id)
                ? prev.filter((id) => id !== commission.soc_id)
                : [...prev, commission.soc_id]
        );
    };

    const toggleSelectAll = () => {
        setSelectedCommissionIds((prev) =>
            allSelectableChecked ? prev.filter((id) => !selectableIds.includes(id)) : selectableIds
        );
    };

    const applyFilters = (event) => {
        event.preventDefault();
        setSelectedCommissionIds([]);
        visitWithFilters({ page: 1 });
    };

    const resetFilters = () => {
        setSearch("");
        setWeek("");
        setPerPage("10");
        setSelectedCommissionIds([]);
        router.get(
            "/panel/reseller-commissions",
            { page: 1, per_page: 10 },
            { preserveState: true, replace: true }
        );
    };

    const createPayout = async () => {
        if (selectedCommissionIds.length === 0) {
            setNotification({
                type: "error",
                message: "Select at least one approved commission.",
            });
            return;
        }

        setBusyKey("create-payout");
        setNotification(null);

        try {
            const response = await window.axios.post("/panel/reseller-commissions/payouts", {
                commission_ids: selectedCommissionIds,
            });
            setNotification({
                type: "success",
                message: response?.data?.message ?? "Payout batch created successfully.",
            });
            window.alert(response?.data?.message ?? "Payout batch created successfully.");
            reloadPage();
        } catch (error) {
            setNotification({
                type: "error",
                message:
                    error?.response?.data?.message ??
                    "Failed to create payout batch.",
            });
        } finally {
            setBusyKey("");
        }
    };

    const markPaid = async (payout) => {
        const referenceNo = window.prompt(
            `Reference number for ${payout.payout_code} (optional):`,
            payout.reference_no ?? ""
        );

        setBusyKey(`paid-${payout.payout_id}`);
        setNotification(null);

        try {
            const response = await window.axios.post(
                `/panel/reseller-commissions/payouts/${payout.payout_id}/mark-paid`,
                {
                    reference_no: referenceNo ?? "",
                }
            );
            setNotification({
                type: "success",
                message: response?.data?.message ?? "Payout marked as paid successfully.",
            });
            window.alert(response?.data?.message ?? "Payout marked as paid successfully.");
            reloadPage();
        } catch (error) {
            setNotification({
                type: "error",
                message:
                    error?.response?.data?.message ??
                    "Failed to mark payout as paid.",
            });
        } finally {
            setBusyKey("");
        }
    };

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div>
                            <h1 className="text-lg md:text-xl font-semibold text-foreground">Reseller Commission</h1>
                            <p className="text-sm text-muted-foreground-1">Commission ledger and payout reconciliation.</p>
                        </div>

                        {notification && (
                            <div className={`rounded-lg border px-4 py-3 text-sm ${
                                notification.type === "success"
                                    ? "border-green-200 bg-green-50 text-green-700"
                                    : "border-red-200 bg-red-50 text-red-700"
                            }`}>
                                {notification.message}
                            </div>
                        )}

                        <form onSubmit={applyFilters} className="rounded-xl border border-card-line bg-card p-4 shadow-2xs">
                            <div className="grid gap-4 md:grid-cols-4">
                                <div className="md:col-span-2">
                                    <label className="mb-1 block text-sm font-medium text-foreground">Search reseller</label>
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search by reseller name or phone"
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-foreground">Week</label>
                                    <input
                                        type="week"
                                        value={week}
                                        onChange={(event) => setWeek(event.target.value)}
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-foreground">Rows</label>
                                    <select
                                        value={perPage}
                                        onChange={(event) => setPerPage(event.target.value)}
                                        className="w-full rounded-lg border border-layer-line bg-white px-3 py-2 text-sm text-foreground focus:outline-none"
                                    >
                                        {[10, 25, 50].map((value) => (
                                            <option key={value} value={value}>
                                                {value} rows
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    type="submit"
                                    className="inline-flex items-center rounded-lg border border-primary-line bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
                                >
                                    Apply Filters
                                </button>
                                <button
                                    type="button"
                                    onClick={resetFilters}
                                    className="inline-flex items-center rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover"
                                >
                                    Reset
                                </button>
                            </div>
                        </form>

                        <div className="space-y-5">
                                <div className="bg-card border border-card-line rounded-xl shadow-2xs overflow-hidden">
                                    <div className="px-5 py-3 border-b border-card-header flex flex-wrap items-center justify-between gap-3">
                                        <div>
                                            <h2 className="font-semibold text-foreground">Commission Ledger</h2>
                                            <p className="text-sm text-muted-foreground-1">
                                                Select approved commissions to include in the next payout batch.
                                            </p>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-3">
                                            <div className="text-sm text-muted-foreground-1">
                                                {selectedCommissionIds.length} selected
                                            </div>
                                            <div className="text-sm text-muted-foreground-1">
                                                {selectedMemberIds.length} reseller batch{selectedMemberIds.length === 1 ? "" : "es"}
                                            </div>
                                            <div className="text-sm font-semibold text-foreground">
                                                {formatCurrency(selectedTotal)}
                                            </div>
                                            <button
                                                type="button"
                                                disabled={busyKey === "create-payout" || selectedCommissionIds.length === 0}
                                                onClick={createPayout}
                                                className="inline-flex items-center rounded-lg border border-primary-line bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                                            >
                                                {busyKey === "create-payout" ? "Creating..." : "Create Payout Batch"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="p-5 overflow-x-auto">
                                        {selectedResellerNames.length > 0 && (
                                            <div className="mb-4 text-sm text-muted-foreground-1">
                                                Group payout creation by reseller: {selectedResellerNames.join(", ")}
                                            </div>
                                        )}
                                        <table className="min-w-full divide-y divide-table-line">
                                            <thead>
                                                <tr>
                                                    <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={allSelectableChecked}
                                                            onChange={toggleSelectAll}
                                                            className="size-4 rounded border border-layer-line"
                                                        />
                                                    </th>
                                                    <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Order</th>
                                                    <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Reseller</th>
                                                    <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Variant</th>
                                                    <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Amount</th>
                                                    <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-table-line">
                                                {commissionRows.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={6} className="py-6 px-4 text-center text-sm text-muted-foreground-2">
                                                            No commission records yet.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    commissionRows.map((commission) => (
                                                        <tr key={commission.soc_id}>
                                                            <td className="py-3 px-4">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedCommissionIds.includes(commission.soc_id)}
                                                                    disabled={!(Number(commission.commission_status) === 1 && !commission.payout_id)}
                                                                    onChange={() => toggleCommission(commission)}
                                                                    className="size-4 rounded border border-layer-line disabled:opacity-40"
                                                                />
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-foreground">
                                                                <div>{commission.sales_order?.so_code ?? "-"}</div>
                                                                <div className="text-xs text-muted-foreground-1">{commission.reseller_code_snapshot}</div>
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-foreground">
                                                                <div>{commission.member?.user?.name ?? "-"}</div>
                                                                <div className="text-xs text-muted-foreground-1">{commission.member?.user?.email ?? "-"}</div>
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-foreground">
                                                                {commission.variant?.product?.prod_name ?? "-"} / {commission.variant?.prod_var_name ?? "-"}
                                                            </td>
                                                            <td className="py-3 px-4 text-sm text-foreground">
                                                                <div>{formatCurrency(commission.commission_amount)}</div>
                                                                <div className="text-xs text-muted-foreground-1">
                                                                    {commission.commission_type_label} {Number(commission.commission_rate)}
                                                                </div>
                                                            </td>
                                                            <td className="py-3 px-4">
                                                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${commissionStatusClass(commission.commission_status)}`}>
                                                                    {commission.commission_status_label}
                                                                </span>
                                                                {commission.payout_id && (
                                                                    <div className="mt-1 text-xs text-muted-foreground-1">
                                                                        Batch: {commission.payout?.payout_code ?? "-"}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="border-t border-card-header px-5 py-4">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="text-sm text-muted-foreground-1">
                                                Showing {commissions?.from ?? 0}-{commissions?.to ?? 0} of {commissions?.total ?? 0}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    disabled={!commissions?.prev_page_url}
                                                    onClick={() => visitWithFilters({ page: (commissions?.current_page ?? 1) - 1 })}
                                                    className="inline-flex items-center rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover disabled:opacity-50"
                                                >
                                                    Previous
                                                </button>
                                                <span className="text-sm text-muted-foreground-1">
                                                    Page {commissions?.current_page ?? 1} of {commissions?.last_page ?? 1}
                                                </span>
                                                <button
                                                    type="button"
                                                    disabled={!commissions?.next_page_url}
                                                    onClick={() => visitWithFilters({ page: (commissions?.current_page ?? 1) + 1 })}
                                                    className="inline-flex items-center rounded-lg border border-layer-line bg-layer px-3 py-2 text-sm font-medium text-layer-foreground hover:bg-layer-hover disabled:opacity-50"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
